import React from 'react';

// Components
import WpChoice from '../choice/WpChoice';
import { WpIcon } from '../icon';

// Constants
import ColorConstants from '../../colors/ColorConstants';

// Styles
import './WpRadioInput.scss';

export interface Props {
  /** ID for the input */
  id: string;
  /** Initial value for the input */
  value: string;
  /** Name for form input */
  name?: string;
  /** Additional hint text to display */
  descriptionText?: string;
  /** Radio button is selected */
  checked?: boolean;
  /** Label for the input */
  label: string;
  /** Data tag for the spec tests */
  qaTest?: string;
  /** Disable the input */
  disabled?: boolean;
  /** Automatically focus the input */
  autoFocus?: boolean;
  /** Callback when value is changed */
  onChange?(value: string): void;
  /** component used to replace label if included */
  component?: React.ReactNode;
}

const WpRadioInput: React.FC<Props> = ({
  id,
  value,
  name,
  descriptionText,
  label,
  checked,
  autoFocus,
  qaTest,
  disabled,
  onChange,
  component,
}: Props) => {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) onChange(event.currentTarget.value);
  };

  const handleClick = () => {
    if (!disabled && onChange) onChange(value);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick();
    }
  };

  const baseClassName = 'WpRadioInput';
  return (
    <WpChoice id={id} label={label} descriptionText={descriptionText} onClick={handleClick} component={component}>
      <div
        className={baseClassName}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
        role="radio"
        aria-checked={checked}
        aria-disabled={disabled}
        data-test={qaTest}
      >
        <WpIcon
          color={ColorConstants.purple[800]}
          prefix={checked ? 'fas' : undefined}
          name={checked ? 'dot-circle' : 'circle'}
        />
        <input
          id={id}
          name={name}
          value={value}
          type="radio"
          checked={checked}
          disabled={disabled}
          onChange={handleChange}
          autoFocus={autoFocus}
          className={`${baseClassName}__input`}
        />
      </div>
    </WpChoice>
  );
};

export default WpRadioInput;
